import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SupplierPage {
  id: string;
  name: string | null;
  slug: string | null;
  sub_category: string | null;
  profile_image_url: string | null;
  is_active: boolean | null;
}

const STORAGE_KEY_PREFIX = 'weddingly_active_page_';

export const useSupplierPages = (supplierProfileId: string | undefined) => {
  const [pages, setPages] = useState<SupplierPage[]>([]);
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supplierProfileId) {
      setPages([]);
      setActivePageId(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchPages = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('supplier_pages')
          .select('id, name, slug, sub_category, profile_image_url, is_active')
          .eq('supplier_id', supplierProfileId)
          .order('created_at', { ascending: true });

        if (fetchError) {
          console.error('Error fetching supplier pages:', fetchError);
          setError(fetchError.message);
          setPages([]);
          setActivePageId(null);
          return;
        }

        const fetchedPages: SupplierPage[] = data || [];
        setPages(fetchedPages);

        if (fetchedPages.length > 0) {
          // Restore persisted active page, or default to first
          const storageKey = STORAGE_KEY_PREFIX + supplierProfileId;
          const storedPageId = localStorage.getItem(storageKey);
          const storedPageExists = storedPageId && fetchedPages.some(p => p.id === storedPageId);

          setActivePageId(storedPageExists ? storedPageId : fetchedPages[0].id);
        } else {
          setActivePageId(null);
        }
      } catch (err) {
        console.error('Error fetching supplier pages:', err);
        setError('Failed to fetch supplier pages');
        setPages([]);
        setActivePageId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, [supplierProfileId]);

  const setActivePage = useCallback((pageId: string) => {
    setActivePageId(pageId);
    if (supplierProfileId) {
      localStorage.setItem(STORAGE_KEY_PREFIX + supplierProfileId, pageId);
    }
  }, [supplierProfileId]);

  const activePage = pages.find(p => p.id === activePageId) || null;
  const hasMultiplePages = pages.length > 1;

  return { pages, activePage, setActivePage, hasMultiplePages, loading, error };
};
